import SendRequest from '../../../shared/services/sendRequest.service';
import { select, takeEvery, put } from 'redux-saga/effects';
import actionTypes from '../../types/savedDraftTypes';
import { RootState } from '../..';
import { setDraftError, setDraftResponseStatus, updatedDraftDeleteCounter, updateDraftId } from '../../actions/savedDraftAction';
import { GLOBAL_URL } from '../../../shared/constants/dataServiceUrl';
import { ERROR, SUCCESS } from '../../../shared/constants/crmConstants';
import { convertToISOStrings, formatSelectedFilter } from '../../../modules/crm/inPatient/service/crmUtils';
import { combineDateAndTime, convertUtcToIst, success } from '../../../utils/utility';
import { ApplicationDetailModel } from '../../models/applicationDetailModel';
import { DEFAULT_DATE_FILTER } from '../../../shared/constants/commonConstants';

export function* saveDraftInfo({ requestPayload, onSuccess, onError }: any): Generator {
  const sendRequest = SendRequest.instance;
  const { selectedDraftId }: any = yield select((state: RootState): any => state.savedDraft);
  const payerId = requestPayload?.payer?.id;
  const providerId = requestPayload?.claimDetails?.providerId;
  const schemeType = requestPayload?.payer?.productCode;
  const memberId =  requestPayload?.selectedBeneficiaryDetail?.memberId || requestPayload?.claimDetails?.memberId
  || requestPayload?.newFormValue?.customerDetail?.memberId;
  const memberName = requestPayload?.newFormValue?.customerDetail?.fullName ||
    requestPayload?.selectedBeneficiaryDetail?.memberName || requestPayload?.claimDetails?.patientName;
  const proposedLineOfTreatmentCheck = requestPayload?.newFormValue?.treatmentDetail?.proposedLineOfTreatment;

  if (Array.isArray(proposedLineOfTreatmentCheck)) {
    requestPayload.newFormValue.treatmentDetail.proposedLineOfTreatment =
    requestPayload?.newFormValue?.treatmentDetail?.proposedLineOfTreatment.join(',');
  }

  try {
    const url = `${GLOBAL_URL.CRM_BASE_URL}Provider/Claim/AddUpdateClaimDraftNew`;
    const saveDraftRequest: any = {
      payerId, providerId, id: selectedDraftId, schemeType,
      memberId, memberName, isActive: true,
      claimJsonData: JSON.stringify(requestPayload),
    };
    const saveDraftResponse: any = yield sendRequest.post(url, saveDraftRequest);
    const { isSuccess, errors = [] } = saveDraftResponse || {};
    if ( isSuccess ) {
      onSuccess();
     } else {
      yield put(setDraftError(errors.join(', ')));
      onError(errors.join(', '));
    }
  } catch (errors: any) {
    onError(errors?.errorMessage);
  }
}

function* fetchDraftList({ payload }: any): Generator {
  const { getDraftList } = payload;
  const sendRequest = SendRequest.instance;
  const {
    filters, globalSearch: searchedValue, filterAction,
    sortingKey, pageIndex, isNextStatus, timeFilter: timeFlter,
    tpaSecondaryFilter: tpaSecondoryFilter, isPreAuthView,
    showTpaStartDateAndTime, showTpaEndDateAndTime, tpaDeskSecondaryFilter,
  }: any = yield select((state: RootState): any => state.selectedInPatientDetail);
  const appDetail: any = yield select((state: RootState): ApplicationDetailModel => state.applicationDetail);
  const isNewTpaDeskEnabled = appDetail.getIsNewTpaDeskEnabled();
  const isPreAuthClaimViewEnabled = appDetail?.getIsPreAuthClaimViewEnabled();
  let request: any = {
    payerId: 99999,
    range: 10,
    startIndex: pageIndex || 1,
  };

  const [searchKeyword, searchKey] = String(searchedValue).split('$$');
  if (searchKeyword && searchKey) {
    request[searchKey] = searchKeyword;
  }
  let centerIdListArray;
  if (filters['providerId']?.length > 0) {
     centerIdListArray = filters['providerId'].map((ele: string): number => Number(ele));
    // adding a list of selected centerIds for fetching the claims count on filtering
      request = {
      ...request,
        CenterIdList: centerIdListArray,
      };
    }
  if (filterAction !== 'All' && !isNextStatus) {
    request.action = filterAction;
  } else {
    delete request.action;
  }
  const { dateFilter } = filters;
  const finalFilters = {
    ...filters,
    dateFilter: (Array?.isArray(dateFilter) && dateFilter?.length > 0)
      ? dateFilter
      : DEFAULT_DATE_FILTER,
  };

  let selectedFilters;
  if (isPreAuthClaimViewEnabled && isNewTpaDeskEnabled) {
    selectedFilters = formatSelectedFilter(finalFilters);

  } else {
     selectedFilters = formatSelectedFilter(filters);
  }  if (selectedFilters?.length > 0) {
    request = {
      ...request,
      filters: selectedFilters,
    };
  }
  if (timeFlter) {
    request = {
      ...request,
      timeFlter,
    };
  }

  if (tpaSecondoryFilter) {
    request = {
      ...request,
      tpaSecondoryFilter,
    };
  }

  request.isSearchEnabled = (searchKeyword && searchKey) || selectedFilters?.length > 0 ? true : false;
  if (process.env.REACT_APP_IS_TPA_DASHBOARD_DISABLED === 'true' && centerIdListArray?.length > 0) {
    request.isSearchEnabled = false;  // in Case of group Providers when the centers are selected
  }
  if (sortingKey) {
    request = {
      ...request,
      sortingKey,
    };
  }
  if (isPreAuthClaimViewEnabled) {
    const startDate = combineDateAndTime(showTpaStartDateAndTime?.date, showTpaStartDateAndTime?.time);
    const endDate = combineDateAndTime(showTpaEndDateAndTime?.date, showTpaEndDateAndTime?.time);
    const convertedIsoDate = convertToISOStrings([startDate, endDate]);

  const commonRequestData = {
      ...((isNewTpaDeskEnabled && isPreAuthClaimViewEnabled) && {
        startDate: convertUtcToIst(convertedIsoDate?.[0]),
        endDate: convertUtcToIst(convertedIsoDate?.[1]),
        isNewTpaDeskEnabled,
      }),
      tpaDeskSecondaryFilter,
    };

    if (isPreAuthView) {
      request = {
        ...request,
        viewType: 'PreAuthView',
        ...commonRequestData,
      };
    } else {
      request = {
        ...request,
        viewType: 'ClaimView',
        ...commonRequestData,
      };
    }
  } else {
    request = {
      ...request,
      viewType: 'DefaultView',
    };
  }

  try {
    const response: any =
      yield sendRequest.post(
        `${GLOBAL_URL.CRM_BASE_URL}Provider/Search/ClaimsDraftDetails`,
        request,
        { isShowErrorInInterceptor: false },
      );
    return getDraftList(response);
  } catch (e) {
  }
}

function* lockUnlockOrDeleteDraftSaga(payload: any): any {
  const {
    draftId,
    isLocked,
    shouldDelete,
  } = payload;
  const sendRequest = SendRequest.instance;
  try {
    const url = `${GLOBAL_URL.CRM_BASE_URL}Provider/Claim/UpdateClaimDraftLockStatus`;
    let requestPayload: any = { id: draftId };

    if (shouldDelete) {
      requestPayload = {
        ...requestPayload,
        isActive: false,
        action: 'delete',
      };
    } else {
      requestPayload = {
        ...requestPayload,
        isLocked,
        action: 'lock',
      };
    }

    if (draftId !== 0) {
      const lockDraftResponse: any = yield sendRequest.post(url, requestPayload);
      const { isSuccess, message } = lockDraftResponse || {};
      if (isSuccess) {
        yield put(updateDraftId(draftId));
        yield put(setDraftResponseStatus(SUCCESS));
        if (message && requestPayload?.action === 'delete') {
          success(message); // to show sucess message when draft deleted succesfully
          yield put(updatedDraftDeleteCounter());
        }
      } else {
        yield put(setDraftResponseStatus(ERROR));
      }
    }
  } catch (errors: any) {
    yield put(setDraftResponseStatus(ERROR));
  }
}

function* deleteDraftInfo({onSuccess, draft, onError}: any): any {
  const payerId = draft?.payer?.id;
  const memberId =  draft?.selectedBeneficiaryDetail?.memberId || draft?.claimDetails?.memberId
  || draft?.newFormValue?.customerDetail?.memberId;
  const memberName =  draft?.selectedBeneficiaryDetail?.memberName || draft?.claimDetails?.patientName
  || draft?.newFormValue?.customerDetail?.fullName;
  const sendRequest = SendRequest.instance;
  try {
    const url = `${GLOBAL_URL.CRM_BASE_URL}Provider/Claim/AddUpdateClaimDraftNew`;
    const deleteDraftResponse: any = yield sendRequest.post(url, {id: draft.id, isActive: false, payerId, memberId, memberName});
    const { errors = [], isSuccess } = deleteDraftResponse || {};
    if (isSuccess) {
       onSuccess();
    } else {
      onError(errors.join(', '));
    }
  } catch (errors: any) {
    onError(errors?.errorMessage);
  }
}

export function* watchSaveDraft(): any {
  yield takeEvery(actionTypes.SUBMIT_DRAFT, saveDraftInfo);
  yield takeEvery(actionTypes.FETCH_DRAFT_LIST, fetchDraftList);
  yield takeEvery(actionTypes.LOCK_UNLOCK_OR_DELETE_DRAFT, lockUnlockOrDeleteDraftSaga);
  yield takeEvery(actionTypes.DELETE_DRAFT, deleteDraftInfo);
}

export default watchSaveDraft;
